(() => {
'use strict';
async function loadXoticLiveDealers({ proxyBase, shopDomain }) {
  const origin = globalThis.location?.origin;
  if (!origin || !/^https?:\/\//i.test(origin)) {
    throw new Error('The dealer locator must run on the Shopify storefront.');
  }
  if (typeof proxyBase !== 'string' || !proxyBase.trim()) {
    throw new Error('The dealer locator app path is missing.');
  }
  if (typeof shopDomain !== 'string' || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopDomain.trim())) {
    throw new Error('The dealer locator shop domain is invalid.');
  }
  const base = new URL(proxyBase, `${origin}/`);
  if (base.origin !== origin || !/^\/apps\/[^/]+\/?$/.test(base.pathname) || base.search || base.hash || base.username || base.password) {
    throw new Error('The dealer locator must use the store’s own app proxy.');
  }
  base.pathname = `${base.pathname.replace(/\/$/, '')}/`;
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 15000);
  const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = value => typeof value === 'string' ? value.trim() || null : null;
  const keyText = value => String(value ?? '').normalize('NFKC').toLocaleLowerCase('en-US').replace(/\s+/gu, ' ').trim();
  const scalarFields = ['name', 'address', 'address2', 'city', 'prov_state', 'country', 'postal_zip', 'phone', 'website'];
  async function request(categoryId, label) {
    const url = new URL('stores/surrounding', base);
    const params = {
      shop: shopDomain.trim(), latitude: '39.909736', longitude: '-97.514648',
      max_distance: '0', limit: '0', calc_distance: '0', record_search: '0',
      usage_surface: 'map_block', distance_unit: 'MI', store_name_like: ''
    };
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
    if (categoryId) url.searchParams.set(`search_filter_${categoryId}`, '1');
    const response = await fetch(url.href, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' }, signal: controller.signal
    });
    if (!response.ok) throw new Error(`The dealer app could not load ${label} (HTTP ${response.status}).`);
    const contentType = response.headers.get('content-type') || '';
    if (!/\bapplication\/(?:[a-z0-9.+-]*\+)?json\b/i.test(contentType)) {
      throw new Error(`The dealer app returned an unexpected response for ${label}.`);
    }
    let payload;
    try { payload = await response.json(); } catch (error) {
      if (controller.signal.aborted) throw error;
      throw new Error(`The dealer app returned unreadable data for ${label}.`);
    }
    if (!isRecord(payload) || !Array.isArray(payload.stores)) {
      throw new Error(`The dealer app returned an invalid dealer list for ${label}.`);
    }
    const seen = new Set();
    for (const record of payload.stores) {
      if (!isRecord(record) || !Number.isSafeInteger(record.store_id) || record.store_id <= 0 || seen.has(record.store_id)) {
        throw new Error(`The dealer app returned an invalid dealer identifier for ${label}.`);
      }
      seen.add(record.store_id);
      if (scalarFields.some(field => record[field] != null && typeof record[field] !== 'string')) {
        throw new Error(`The dealer app returned invalid dealer fields for ${label}.`);
      }
    }
    return payload.stores;
  }
  try {
    const [all, distributorRows, retailerRows] = await Promise.all([
      request(null, 'all dealers'), request(1381, 'distributors'), request(1382, 'retailers and installers')
    ]);
    const allIds = new Set(all.map(record => record.store_id));
    const distributors = new Set(distributorRows.map(record => record.store_id));
    const retailers = new Set(retailerRows.map(record => record.store_id));
    if ([...distributors, ...retailers].some(id => !allIds.has(id))) {
      throw new Error('Dealer records changed while loading. Please try again.');
    }
    const aliases = new Map([
      ['united states', 'US'], ['united states of america', 'US'], ['usa', 'US'], ['u.s.a.', 'US'],
      ['canada', 'CA'], ['united kingdom', 'GB'], ['uk', 'GB'], ['great britain', 'GB'],
      ['australia', 'AU'], ['new zealand', 'NZ'], ['united arab emirates', 'AE'], ['uae', 'AE']
    ]);
    const codes = 'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ');
    let names;
    try { names = new Intl.DisplayNames(['en'], { type: 'region' }); } catch {}
    for (const code of codes) {
      aliases.set(code.toLowerCase(), code);
      if (names) aliases.set(keyText(names.of(code)), code);
    }
    const coordinate = (value, min, max) => {
      if (typeof value !== 'number' && typeof value !== 'string') return null;
      if (typeof value === 'string' && !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) return null;
      const number = Number(value);
      return Number.isFinite(number) && number >= min && number <= max ? number : null;
    };
    const validWebsite = value => {
      if (!value) return null;
      try {
        const url = new URL(value);
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || !url.hostname.includes('.') || /\s/u.test(value)) return null;
        return url.href;
      } catch { return null; }
    };
    const websiteRank = value => {
      const url = new URL(value);
      return [url.pathname === '/' && !url.search && !url.hash ? 0 : 1, value.length];
    };
    const preferWebsite = (candidate, current) => {
      if (!candidate) return false;
      if (!current) return true;
      const a = websiteRank(candidate), b = websiteRank(current);
      return a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
    };
    const invalidAddress = value => !/[\p{L}\p{N}]/u.test(value) || /^(?:n\/?a|none|null|unknown|tbd|not (?:provided|available))$/i.test(value);
    const grouped = new Map();
    for (const raw of all) {
      const issues = [];
      const name = text(raw.name);
      if (!name) throw new Error(`The dealer app returned a dealer without a name (record ${raw.store_id}).`);
      const address = text(raw.address), address2 = text(raw.address2), city = text(raw.city);
      const region = text(raw.prov_state), postalCode = text(raw.postal_zip), country = text(raw.country);
      const countryCode = country ? aliases.get(keyText(country)) || null : null;
      const lat = coordinate(raw.lat, -90, 90), lng = coordinate(raw.lng, -180, 180);
      if (!address) issues.push('Missing street address; verify display address.');
      else if (invalidAddress(address)) issues.push('Invalid street address; verify display address.');
      if (!city) issues.push('Missing city; verify display address.');
      else if (invalidAddress(city)) issues.push('Invalid city; verify display address.');
      if (!region) issues.push('Missing region; verify display address.');
      if (!country) issues.push('Missing country; verify display address.');
      else if (!countryCode) issues.push('Country could not be matched; verify display address.');
      if (!postalCode) issues.push('Missing postal code.');
      else if (countryCode === 'US' && !/^\d{5}(?:-\d{4})?$/.test(postalCode)) issues.push('US postal code is not a ZIP code; source value retained for review.');
      else if (countryCode === 'CA' && !/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(postalCode)) issues.push('Canadian postal code is invalid; source value retained for review.');
      if (lat === null || lng === null) issues.push('Missing or invalid coordinates; verify display address.');
      const sourceWebsite = text(raw.website), website = validWebsite(sourceWebsite);
      if (sourceWebsite && !website) issues.push('Malformed website; source value is not linked.');
      else if (!sourceWebsite) issues.push('Missing website.');
      const sourcePhone = text(raw.phone), digits = (sourcePhone || '').replace(/\D/g, '');
      const phoneFormat = sourcePhone && /^[+()\d\s./-]+$/u.test(sourcePhone);
      const phoneLength = countryCode === 'US' || countryCode === 'CA'
        ? digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
        : digits.length >= 7 && digits.length <= 15;
      const phone = phoneFormat && phoneLength ? sourcePhone : null;
      if (sourcePhone && !phone) issues.push('Invalid or incomplete phone number; source value is not dialled.');
      else if (!sourcePhone) issues.push('Missing phone.');
      const categories = [];
      if (distributors.has(raw.store_id)) categories.push('distributor');
      if (retailers.has(raw.store_id)) categories.push('retailer-installer');
      if (!categories.length) issues.push('No public category tag is assigned; do not assume distributor or retailer/installer.');
      const dealer = {
        id: raw.store_id, sourceIds: [raw.store_id], name, address, address2, city, region,
        postalCode, country, countryCode, lat, lng, website, phone, categories, dataIssues: issues
      };
      const key = JSON.stringify([name, address, city].map(keyText));
      const existing = grouped.get(key);
      if (!existing) { grouped.set(key, dealer); continue; }
      existing.sourceIds.push(raw.store_id);
      existing.id = Math.min(existing.id, raw.store_id);
      existing.categories = [...new Set([...existing.categories, ...categories])];
      existing.dataIssues = [...new Set([...existing.dataIssues, ...issues, 'Duplicate source records merged for display; source records retained.'])];
      if (preferWebsite(website, existing.website)) existing.website = website;
      if (!existing.phone && phone) existing.phone = phone;
      for (const field of ['address2', 'region', 'postalCode', 'country', 'countryCode', 'lat', 'lng']) {
        if (existing[field] === null && dealer[field] !== null) existing[field] = dealer[field];
      }
      if (existing.lat !== null && lat !== null && existing.lng !== null && lng !== null && (Math.abs(existing.lat - lat) > 0.0001 || Math.abs(existing.lng - lng) > 0.0001)) {
        existing.dataIssues = [...new Set([...existing.dataIssues, 'Duplicate source coordinates disagree; verify display address.'])];
      }
    }
    return [...grouped.values()].map(dealer => ({ ...dealer, sourceIds: dealer.sourceIds.sort((a, b) => a - b) }));
  } catch (error) {
    controller.abort();
    if (timedOut) throw new Error('The dealer app took too long to respond. Please try again.');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

class XoticDealerFinder extends HTMLElement {
  connectedCallback() {
    if(this._cleanup)return;
    const template=this.querySelector('template');
    const translationsElement=this.querySelector('script[type="application/json"][data-xotic-i18n]');
    if(!template||!translationsElement)return;
    let translations;
    try{translations=JSON.parse(translationsElement.textContent);}catch{return;}
    const translationDecoder=document.createElement('textarea');
    for(const key of Object.keys(translations)){translationDecoder.innerHTML=String(translations[key]);translations[key]=translationDecoder.value;}
    const t=(key,variables={})=>String(translations[key]??key).replace(/\{([a-z_]+)\}/g,(match,name)=>Object.prototype.hasOwnProperty.call(variables,name)?String(variables[name]):match);
    const root=this.shadowRoot||this.attachShadow({mode:'open'});
    root.replaceChildren(template.content.cloneNode(true));
const $ = selector => root.querySelector(selector);
const L = window.L;
const hasCoordinates = dealer => Number.isFinite(dealer.lat) && Number.isFinite(dealer.lng);
let disposed = false, resizeObserver = null, activeSearch = null;
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
let dealers = [], filtered = [], selectedId = null, category = 'all', textMatches = null, origin = null, map = null, markerLayer = null, originLayer = null, markers = new Map(), searchSequence = 0;
const labels = {'retailer-installer':t('retailer_installer'), distributor:t('distributor')};

function distanceMiles(a, b) {
  const rad = (v) => v * Math.PI / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 3958.7613 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
function addressLines(d) {
  let street = [d.address, d.address2].filter(Boolean).join(', ');
  if (street.toLowerCase().includes('united states')) return [street, ''];
  const postal = d.countryCode === 'US' && !/^\d{5}(-\d{4})?$/.test(d.postalCode || '') ? '' : d.postalCode;
  return [street, [d.city, d.region].filter(Boolean).join(', ') + (postal ? ` ${postal}` : '')];
}
function directionsUrl(d) {
  const destination = hasCoordinates(d) ? `${d.lat},${d.lng}` : [d.address,d.address2,d.city,d.region,d.postalCode,d.country].filter(Boolean).join(', ');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}
function safeWebsite(value) {
  try { const u = new URL(value); return ['https:', 'http:'].includes(u.protocol) ? u.href : null; } catch { return null; }
}
function safePhone(value) {
  const digits=String(value || '').replace(/\D/g,'');
  return digits.length>=7 && digits.length<=15;
}
function status(message, error = false) {
  const el = $('#status'); el.textContent = message; el.hidden = !message; el.classList.toggle('error', error);
}
function busy(active) {
  $('#find-button').disabled = active;
  $('#find-button').innerHTML = active ? escapeHtml(t('finding_dealers')) : `${escapeHtml(t('find_dealers'))} <span aria-hidden="true">→</span>`;
  $('#use-location').disabled = active;
}
function makeIcon(number, selected) {
  return L.divIcon({className:`map-pin${selected ? ' is-selected' : ''}`,html:`<span><b>${number}</b></span>`,iconSize:[26,32],iconAnchor:[13,32],popupAnchor:[0,-32]});
}
function initMap() {
  if (!L || !L.markerClusterGroup) { $('#map-error').hidden = false; return; }
  map = L.map($('#map'), {zoomControl:false,scrollWheelZoom:false,minZoom:2,maxZoom:18}).setView([40.4,-98.1],4);
  L.control.zoom({position:'bottomright'}).addTo(map);
  let failed = 0, loaded = 0;
  const tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {maxZoom:19,attribution:'&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'}).addTo(map);
  tiles.on('tileerror', () => { failed++; if (failed > 4 && !loaded) $('#map-error').hidden = false; });
  tiles.on('tileload', () => { loaded++; $('#map-error').hidden = true; });
  markerLayer = L.markerClusterGroup({maxClusterRadius:36,showCoverageOnHover:false,disableClusteringAtZoom:10,iconCreateFunction:cluster=>L.divIcon({html:`<span>${cluster.getChildCount()}</span>`,className:'dealer-cluster',iconSize:[38,38]})}).addTo(map);
  originLayer = L.layerGroup().addTo(map);
}
function fitMap() {
  if (!map) return;
  map.invalidateSize();
  const points = filtered.filter(hasCoordinates).map(d => [d.lat,d.lng]);
  if (origin) points.push([origin.lat,origin.lng]);
  if (points.length) map.fitBounds(L.latLngBounds(points), {padding:[45,60],maxZoom:11,animate:false});
  else map.setView([40.4,-98.1],4);
}
function renderMap() {
  if (!map) return;
  markerLayer.clearLayers(); originLayer.clearLayers(); markers.clear();
  filtered.forEach((d,i) => {
    if (!hasCoordinates(d)) return;
    const marker = L.marker([d.lat,d.lng], {icon:makeIcon(i+1,d.id===selectedId),title:d.name,alt:t('marker_alt',{name:d.name,number:i+1}),riseOnHover:true}).addTo(markerLayer);
    const [street,city] = addressLines(d);
    marker.bindPopup(`<strong>${escapeHtml(d.name)}</strong>${escapeHtml(street)}<br>${escapeHtml(city)}<br><a href="${directionsUrl(d)}" target="_blank" rel="noopener">${escapeHtml(t('get_directions'))} ↗</a>`,{maxWidth:240});
    marker.on('click', () => selectDealer(d.id, false, true)); markers.set(d.id,marker);
  });
  if (origin) L.circleMarker([origin.lat,origin.lng],{radius:6,color:'#fff',weight:3,fillColor:'#204ed8',fillOpacity:1}).bindTooltip(escapeHtml(origin.label)).addTo(originLayer);
  fitMap();
}
function selectDealer(id, moveMap = true, scrollCard = false) {
  selectedId = id;
  if(moveMap && markers.has(id) && matchMedia('(max-width: 800px)').matches) {
    $('#locator').dataset.view='map';
    root.querySelectorAll('.mobile-view button').forEach(b=>{b.classList.toggle('active',b.dataset.view==='map');b.setAttribute('aria-pressed',String(b.dataset.view==='map'));});
    if(map) map.invalidateSize();
  }
  root.querySelectorAll('.dealer-card').forEach(el => {
    const selected = Number(el.dataset.id) === id;
    el.classList.toggle('selected', selected);
    el.querySelector('.select-dealer').setAttribute('aria-pressed', String(selected));
    if (selected && scrollCard && $('#locator').dataset.view==='list') {
      const list = $('#results');
      list.scrollTo({top:el.offsetTop-list.offsetTop,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    }
  });
  filtered.forEach((d,i) => { const m=markers.get(d.id); if(m) {m.setIcon(makeIcon(i+1,d.id===id));m.setZIndexOffset(d.id===id?1000:0);} });
  const dealer = filtered.find(d=>d.id===id), marker = markers.get(id);
  if (marker && moveMap) { map.setView([dealer.lat,dealer.lng],Math.max(map.getZoom(),9),{animate:!matchMedia('(prefers-reduced-motion: reduce)').matches}); markerLayer.zoomToShowLayer(marker,()=>marker.openPopup()); }
}
function render() {
  const country = $('#country').value, radius = $('#radius').value;
  filtered = dealers.filter(d => (country==='all' || d.countryCode===country) && (category==='all' || d.categories.includes(category)) && (!textMatches || textMatches.has(d.id)));
  if (origin) {
    filtered = filtered.map(d=>({...d,distance:hasCoordinates(d)?distanceMiles(origin,d):null})).filter(d=>radius==='all'||(d.distance!==null&&d.distance<=Number(radius)));
  }
  const sortDistance = $('#sort').value==='distance' && origin;
  filtered.sort(sortDistance ? (a,b)=>(a.distance??Infinity)-(b.distance??Infinity) : (a,b)=>a.name.localeCompare(b.name,undefined,{sensitivity:'base'}));
  if (!filtered.some(d=>d.id===selectedId)) selectedId = filtered[0]?.id ?? null;
  $('#result-count').textContent = t(origin?(filtered.length===1?'dealer_nearby':'dealers_nearby'):(filtered.length===1?'dealer_found':'dealers_found'),{count:filtered.length});
  $('#all-count').textContent = dealers.length;
  $('#map-region').textContent = origin ? origin.label.toUpperCase() : country==='US'?t('united_states'):country==='CA'?t('canada'):t('united_states_canada');
  if (!filtered.length) {
    $('#results').innerHTML = `<div class="empty-state"><h3>${escapeHtml(t('no_dealers'))}</h3><p>${escapeHtml(t('try_wider_search'))}</p><button id="reset-filters" type="button">${escapeHtml(t('reset_filters'))}</button></div>`;
    $('#reset-filters').addEventListener('click', resetAll);
  } else {
    $('#results').innerHTML = filtered.map((d,i) => {
      const [street,city] = addressLines(d), website = safeWebsite(d.website);
      const addressUncertain=d.dataIssues?.some(issue=>/verify before correcting|pairing needs verification|verify display address/.test(issue));
      const categoryText = d.categories.length ? d.categories.map(c=>labels[c]).join(' · ') : t('authorized_dealer');
      return `<article class="dealer-card${d.id===selectedId?' selected':''}" data-id="${d.id}"><div class="card-top"><h3><button class="select-dealer" type="button" aria-label="${escapeHtml(t(hasCoordinates(d)?'show_on_map':'select_dealer',{name:d.name}))}" aria-pressed="${d.id===selectedId}">${escapeHtml(d.name)}</button></h3><span class="dealer-number" aria-hidden="true">${i+1}</span></div><p class="dealer-category">${escapeHtml(categoryText)}</p><p class="dealer-address">${escapeHtml(street)}<br>${escapeHtml(city)}</p>${addressUncertain?`<p class="address-note">${escapeHtml(t('confirm_address'))}</p>`:''}${origin&&d.distance!==null?`<span class="dealer-distance">${escapeHtml(t(d.distance<1?'distance_less_than_one':'distance_miles',{distance:Math.round(d.distance)}))}</span>`:''}${safePhone(d.phone)?`<a class="dealer-phone" href="tel:${escapeHtml(d.phone.replace(/[^\d+]/g,''))}">${escapeHtml(d.phone)} ↗</a>`:''}<div class="dealer-actions"><a href="${directionsUrl(d)}" target="_blank" rel="noopener">${escapeHtml(t('get_directions'))} <span aria-hidden="true">↗</span></a>${website?`<a href="${escapeHtml(website)}" target="_blank" rel="noopener">${escapeHtml(t('visit_website'))} <span aria-hidden="true">↗</span></a>`:''}</div></article>`;
    }).join('');
    $('#results').querySelectorAll('.dealer-card').forEach(card => {
      card.addEventListener('click', e => { if (!e.target.closest('a')) selectDealer(Number(card.dataset.id)); });
    });
  }
  $('#results').scrollTop=0;
  renderMap();
}
function enableDistance() {
  $('#sort option[value=distance]').disabled = !origin;
  if (origin) $('#sort').value='distance';
  else if ($('#sort').value==='distance') $('#sort').value='name';
  $('#search-help').textContent=origin?t('straight_line_help'):t('default_help');
}
function clearSearch() {
  searchSequence++; activeSearch?.abort(); activeSearch=null; busy(false); $('#search').value=''; $('#clear-search').hidden=true; textMatches=null; origin=null; status(''); enableDistance(); render();
}
function resetAll() {
  category='all';$('#country').value='all';$('#radius').value='100';
  root.querySelectorAll('[data-category]').forEach(b=>{b.classList.toggle('active',b.dataset.category==='all');b.setAttribute('aria-pressed',String(b.dataset.category==='all'));});
  clearSearch();
}
async function runSearch(event) {
  if(event) event.preventDefault();
  activeSearch?.abort();
  const query=$('#search').value.trim(), normalized=normalize(query), sequence=++searchSequence;
  busy(false); $('#clear-search').hidden=!query; origin=null;textMatches=null;status('');
  if (!query) {enableDistance();render();return;}
  const dealerMatches=dealers.filter(d=>normalize(d.name).includes(normalized));
  if(dealerMatches.length) {textMatches=new Set(dealerMatches.map(d=>d.id));status(t('matching_names',{query}));enableDistance();render();return;}
  busy(true);
  try {
    const controller=new AbortController(); activeSearch=controller; const timeout=setTimeout(()=>controller.abort(),9000);
    let response;
    const countryFilter=$('#country').value==='all'?'countrycode=US&countrycode=CA':`countrycode=${$('#country').value}`;
    try {response=await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5&${countryFilter}`,{signal:controller.signal});} finally {clearTimeout(timeout);}
    if(!response.ok) throw new Error('Geocoding unavailable');
    const result=await response.json();
    if(disposed||sequence!==searchSequence) return;
    const selectedCountry=$('#country').value.toLowerCase();
    const place=Array.isArray(result.features)?result.features.find(f=>Array.isArray(f?.geometry?.coordinates)&&f.geometry.coordinates.length>=2&&Number.isFinite(f.geometry.coordinates[0])&&Number.isFinite(f.geometry.coordinates[1])&&Math.abs(f.geometry.coordinates[0])<=180&&Math.abs(f.geometry.coordinates[1])<=90&&['us','ca'].includes(f.properties?.countrycode?.toLowerCase())&&(selectedCountry==='all'||f.properties.countrycode.toLowerCase()===selectedCountry)):null;
    if(!place) {const matches=dealers.filter(d=>normalize([d.city,d.region,d.postalCode].join(' ')).includes(normalized));textMatches=new Set(matches.map(d=>d.id));status(matches.length?t('matching_records',{query}):t('location_not_found',{query}),!matches.length);}
    else {
      const p=place.properties;
      origin={lat:place.geometry.coordinates[1],lng:place.geometry.coordinates[0],label:[p.name||p.city||query,p.state].filter(Boolean).join(', ')};
      status(t('searching_near',{location:origin.label}));
    }
  } catch {
    if(disposed||sequence!==searchSequence) return;
    const matches=dealers.filter(d=>normalize([d.name,d.address,d.city,d.region,d.postalCode].join(' ')).includes(normalized));
    textMatches=new Set(matches.map(d=>d.id));
    status(matches.length?t('nearby_unavailable_matches'):t('nearby_unavailable'),true);
  } finally {if(!disposed&&sequence===searchSequence) {busy(false);enableDistance();render();}}
}
function useLocation() {
  if(!navigator.geolocation) {status(t('location_unsupported'),true);return;}
  activeSearch?.abort(); const sequence=++searchSequence; busy(true);status(t('location_waiting'));
  navigator.geolocation.getCurrentPosition(pos=>{
    if(disposed||sequence!==searchSequence)return;
    origin={lat:pos.coords.latitude,lng:pos.coords.longitude,label:t('your_location')};textMatches=null;$('#search').value='';$('#clear-search').hidden=false;busy(false);enableDistance();status(t('showing_near_location'));render();
  },()=>{if(disposed||sequence!==searchSequence)return;busy(false);status(t('location_unavailable'),true);},{enableHighAccuracy:false,timeout:12000,maximumAge:300000});
}
$('#search-form').addEventListener('submit',runSearch);
$('#search').addEventListener('input',()=>{$('#clear-search').hidden=!$('#search').value&&!origin;});
$('#clear-search').addEventListener('click',clearSearch);
$('#country').addEventListener('change',()=>{status('');render();});
$('#radius').addEventListener('change',()=>{if(origin)render();});
$('#sort').addEventListener('change',render);
$('#use-location').addEventListener('click',useLocation);
$('#reset-map').addEventListener('click',fitMap);
root.querySelectorAll('[data-category]').forEach(button=>button.addEventListener('click',()=>{
  category=button.dataset.category;
  root.querySelectorAll('[data-category]').forEach(b=>{b.classList.toggle('active',b===button);b.setAttribute('aria-pressed',String(b===button));});render();
}));
root.querySelectorAll('[data-view]').forEach(button=>{if(button.tagName==='BUTTON')button.addEventListener('click',()=>{
  $('#locator').dataset.view=button.dataset.view;
  root.querySelectorAll('.mobile-view button').forEach(b=>{b.classList.toggle('active',b===button);b.setAttribute('aria-pressed',String(b===button));});
  if(button.dataset.view==='map') requestAnimationFrame(fitMap);
  else requestAnimationFrame(()=>{const card=$('#results .dealer-card.selected');if(card)$('#results').scrollTop=card.offsetTop-$('#results').offsetTop;});
});});
this._cleanup=()=>{disposed=true;searchSequence++;activeSearch?.abort();resizeObserver?.disconnect();if(map){map.remove();map=null;}markers.clear();};
status(t('loading_dealers'));
loadXoticLiveDealers({proxyBase:this.dataset.proxyBase,shopDomain:this.dataset.shopDomain}).then(data=>{
  if(disposed)return;
  dealers=data;
  dealers.forEach(dealer=>{if(normalize(dealer.name)==='farmtuned diesel auto'&&dealer.city==='Cuba'&&dealer.region==='Montana'&&dealer.postalCode==='65453')dealer.dataIssues.push('Source city, region and postal code pairing needs verification; verify before correcting.');});
  initMap();status('');render();
  resizeObserver=new ResizeObserver(()=>{if(map&&!disposed)map.invalidateSize();});resizeObserver.observe($('#map'));
}).catch(()=>{if(disposed)return;status(t('dealer_load_error'),true);$('#result-count').textContent=t('dealers_unavailable');$('#results').innerHTML=`<div class="empty-state"><h3>${escapeHtml(t('need_help'))}</h3><a href="https://www.xotic-performance.com/pages/contact">${escapeHtml(t('contact_support'))} ↗</a></div>`;});
  }
  disconnectedCallback(){this._cleanup?.();this._cleanup=null;}
}
if(!customElements.get('xotic-dealer-finder'))customElements.define('xotic-dealer-finder',XoticDealerFinder);
})();
